<template>
  <Teleport to="body">
    <div v-if="show" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="show = false"></div>
      
      <!-- Modal -->
      <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div class="text-center mb-6">
          <div class="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span class="text-3xl">🔐</span>
          </div>
          <h2 class="text-xl font-bold text-gray-900">Admin Authentication Required</h2>
          <p class="text-gray-500 mt-1">Enter your 2FA code to access admin features</p>
        </div>
        
        <div v-if="statusMessage" class="mb-4 p-3 rounded-lg text-sm" :class="statusClass">
          {{ statusMessage }}
        </div>
        
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Authenticator Code</label>
            <input
              v-model="code"
              type="text"
              inputmode="numeric"
              maxlength="6"
              class="w-full px-4 py-3 border border-gray-300 rounded-xl text-center text-2xl tracking-[0.5em] font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition"
              placeholder="000000"
              @keyup.enter="verify"
              ref="codeInput"
            >
            <p class="text-xs text-gray-500 mt-2 text-center">Open your authenticator app and enter the 6-digit code</p>
          </div>
          
          <button
            @click="verify"
            :disabled="loading || code.length !== 6"
            class="w-full py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium"
          >
            <span v-if="loading" class="flex items-center justify-center gap-2">
              <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verifying...
            </span>
            <span v-else>Verify & Continue</span>
          </button>
          
          <button
            @click="show = false"
            class="w-full py-2 text-gray-500 hover:text-gray-700 transition text-sm"
          >
            Cancel
          </button>
        </div>
        
        <div class="mt-4 text-center">
          <router-link to="/profile" @click="show = false" class="text-sm text-primary-600 hover:text-primary-700">
            Need to set up 2FA? Go to Profile → Security
          </router-link>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import api from '@/api'

const show = ref(false)
const code = ref('')
const loading = ref(false)
const statusMessage = ref('')
const statusClass = ref('')
const codeInput = ref<HTMLInputElement>()

let retryRequest: (() => void) | null = null

function openModal(retry?: () => void) {
  show.value = true
  code.value = ''
  statusMessage.value = ''
  retryRequest = retry || null
  nextTick(() => {
    codeInput.value?.focus()
  })
}

function closeModal() {
  show.value = false
  code.value = ''
  statusMessage.value = ''
}

async function verify() {
  if (code.value.length !== 6) return
  
  loading.value = true
  statusMessage.value = ''
  
  try {
    const res = await api.post('/auth/admin-2fa/verify', { code: code.value })
    
    if (res.data.success) {
      // Store admin 2FA token
      localStorage.setItem('admin_2fa_token', res.data.token)
      
      statusMessage.value = 'Verified! Redirecting...'
      statusClass.value = 'bg-green-50 text-green-700 border border-green-200'
      
      // Retry the failed request if available
      if (retryRequest) {
        setTimeout(() => {
          closeModal()
          retryRequest?.()
        }, 500)
      } else {
        setTimeout(closeModal, 500)
      }
    } else {
      statusMessage.value = res.data.message || 'Invalid code'
      statusClass.value = 'bg-red-50 text-red-700 border border-red-200'
      code.value = ''
      codeInput.value?.focus()
    }
  } catch (e: any) {
    statusMessage.value = e.response?.data?.message || 'Verification failed'
    statusClass.value = 'bg-red-50 text-red-700 border border-red-200'
    code.value = ''
  } finally {
    loading.value = false
  }
}

function handleAdmin2FARequired(event: CustomEvent) {
  statusMessage.value = ''
  openModal()
}

onMounted(() => {
  window.addEventListener('admin-2fa-required', handleAdmin2FARequired as EventListener)
})

onUnmounted(() => {
  window.removeEventListener('admin-2fa-required', handleAdmin2FARequired as EventListener)
})

// Expose for programmatic use
defineExpose({ openModal, closeModal })
</script>
